import { useEffect, useMemo, useRef, useState } from "react";
import { Clock } from "lucide-react";

// T63: countdown to a scheduled banner's real end. Server time: offset = server now - device clock (set when the config arrived).
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
export function fmtLeft(ms) {
  // rounded up (to the minute; to the second in the final hour) so exactly 48 h reads "2 days 0 hrs", never "1 day 23 hrs"
  const s = Math.max(0, ms > 3600000 ? Math.ceil(ms / 60000) * 60 : Math.ceil(ms / 1000));
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (s >= 86400) return `${plural(d, "day")} ${plural(h, "hr")}`;
  if (s >= 3600) return `${plural(h, "hr")} ${plural(m, "min")}`;
  return `${plural(m, "min")} ${plural(sec, "sec")}`;
}
const endsText = (iso) => new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Sydney", weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit" });

export function HeroCountdown({ cd, onEnd, onShow, className = "" }) {
  const offset = useMemo(() => (cd?.offset ?? (cd?.now ? Date.parse(cd.now) - Date.now() : 0)), [cd]);
  const [t, setT] = useState(() => Date.now() + offset);
  const ended = useRef(false);
  const end = cd ? Date.parse(cd.ends_at) : 0, from = cd ? Date.parse(cd.show_from) : 0;
  useEffect(() => {
    if (!cd) return undefined;
    ended.current = false;
    let id;
    const tick = () => {
      const now = Date.now() + offset;
      setT(now);
      const left = end - now;
      if (left <= 0) { if (!ended.current) { ended.current = true; onEnd?.(); } return; }
      // per second in the final hour, otherwise on the next minute boundary of the time left; wake exactly at show_from
      const step = now < from ? Math.min(from - now, 2 ** 31 - 1) : left <= 3600000 ? (left % 1000 || 1000) + 5 : (left % 60000 || 60000) + 5;
      id = setTimeout(tick, Math.min(step, left));
    };
    tick();
    return () => clearTimeout(id);
  }, [cd, offset, end, from, onEnd]);
  const vis = !!cd && t >= from && t < end;
  useEffect(() => { onShow?.(vis); }, [vis, onShow]);
  if (!vis) return null;
  return (
    <p className={`inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-0.5 rounded-full border border-white/25 bg-[rgba(8,20,45,0.72)] px-4 py-2 text-sm font-semibold text-white shadow-[0_2px_10px_rgba(0,0,0,0.25)] backdrop-blur-sm motion-safe:animate-[rs-pop_.35s_ease-out] ${className}`}
      data-testid="hero-countdown" data-cd-placement={cd.placement}>
      <Clock size={16} aria-hidden="true" className="shrink-0 text-[#B4D8FF]" />
      <span className="sr-only" data-testid="hero-countdown-sr">{`${cd.label}: ends ${endsText(cd.ends_at)} Sydney time`}</span>
      <span aria-hidden="true" data-testid="hero-countdown-label">{cd.label}</span>
      <span aria-hidden="true" className="tabular-nums text-[#FFD27A]" data-testid="hero-countdown-time">{fmtLeft(end - t)}</span>
    </p>
  );
}
